import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { AuthProvider, useAuth, setGlobal402Handler } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { SecurityProvider } from '@/contexts/SecurityContext';
import { BillingProvider } from '@/contexts/BillingContext';
import SecurityModals from '@/components/security/SecurityModals';
import SessionTimeoutWarning from '@/components/SessionTimeoutWarning';
import { PlanLimitProvider, usePlanLimit } from '@/components/PlanLimitModal';
import { Toaster } from '@/components/ui/sonner';
import CookieBanner from '@/components/CookieBanner';
import { OfflineBanner } from '@/components/OfflineBanner';
import { initPostHog, posthogOptIn, posthogOptOut } from '@/lib/posthog';
import '@/App.css';

// 404 Not Found Page
function NotFoundPage() {
  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4">
      <div className="text-center">
        <p className="text-8xl font-bold text-teal-500 mb-4">404</p>
        <h1 className="text-2xl font-bold text-white mb-3">Page not found</h1>
        <p className="text-slate-400 mb-8">
          This link may have expired or the page no longer exists.
        </p>
        <div className="flex gap-4 justify-center">
          <a href="/" className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium">
            Go Home
          </a>
          <a href="mailto:support@safekept.co.uk" className="px-6 py-3 border border-slate-600 text-slate-300 hover:text-white rounded-xl font-medium">
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
}

// Referral redirect component
function ReferralRedirect() {
  const { code } = useParams();
  return <Navigate to={`/referred?ref=${code}`} replace />;
}

const HomePage = lazy(() => import('@/pages/marketing/HomePage'));
const DeathNotificationPage = lazy(() => import('@/pages/DeathNotificationPage'));
const HowItWorksPage = lazy(() => import('@/pages/marketing/HowItWorksPage'));
const PricingPage = lazy(() => import('@/pages/marketing/PricingPage'));
const SecurityPage = lazy(() => import('@/pages/marketing/SecurityPage'));
const AboutPage = lazy(() => import('@/pages/marketing/AboutPage'));
const PartnersPage = lazy(() => import('@/pages/marketing/PartnersPage'));
const CustomersPage = lazy(() => import('@/pages/marketing/CustomersPage'));
const ContactPage = lazy(() => import('@/pages/marketing/ContactPage'));
const ResearchPage = lazy(() => import('@/pages/marketing/ResearchPage'));
const BlogIndexPage = lazy(() => import('@/pages/marketing/BlogIndexPage'));
const BlogPostPage = lazy(() => import('@/pages/marketing/BlogPostPage'));
const PrivacyPolicy = lazy(() => import('@/pages/legal/PrivacyPolicy'));
const Terms = lazy(() => import('@/pages/legal/Terms'));
const CookiePolicy = lazy(() => import('@/pages/legal/CookiePolicy'));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const SignupPage = lazy(() => import('@/pages/auth/SignupPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const AuthCallbackPage = lazy(() => import('@/pages/auth/AuthCallbackPage'));
const MFASetupPage = lazy(() => import('@/pages/auth/MFASetupPage'));
const MFAChallengePage = lazy(() => import('@/pages/auth/MFAChallengePage'));
const OnboardingPage = lazy(() => import('@/pages/app/OnboardingPage'));
const DashboardPage = lazy(() => import('@/pages/app/DashboardPage'));
const ActivateTrialPage = lazy(() => import('@/pages/app/ActivateTrialPage'));
const ActivateEstatePage = lazy(() => import('@/pages/app/ActivateEstatePage')); // SECTION 8
const ExecutorDashboardPage = lazy(() => import('@/pages/app/ExecutorDashboardPage'));
const ExecutorExtendPage = lazy(() => import('@/pages/app/ExecutorExtendPage')); // SECTION 20
const VaultPage = lazy(() => import('@/pages/app/VaultPage'));
const CryptoPage = lazy(() => import('@/pages/app/CryptoPage'));
const DocumentsPage = lazy(() => import('@/pages/app/DocumentsPage'));
const SubscriptionsPage = lazy(() => import('@/pages/app/SubscriptionsPage'));
const ExecutorsPage = lazy(() => import('@/pages/app/ExecutorsPage'));
const EstatePage = lazy(() => import('@/pages/app/EstatePage'));
const SettingsPage = lazy(() => import('@/pages/app/SettingsPage'));
const AdminPage = lazy(() => import('@/pages/app/AdminPage'));
const AcceptInvitation = lazy(() => import('@/pages/app/AcceptInvitation'));
const IntegrationsPage = lazy(() => import('@/pages/app/IntegrationsPage'));
const ExecutorInvitePage = lazy(() => import('@/pages/ExecutorInvitePage'));

// SEO Guide Pages
const HowToNotifyBanksGuide = lazy(() => import('@/pages/marketing/guide/HowToNotifyBanksGuide'));
const TellUsOnceGuide = lazy(() => import('@/pages/marketing/guide/TellUsOnceGuide'));
const GrantOfProbateGuide = lazy(() => import('@/pages/marketing/guide/GrantOfProbateGuide'));
const ExecutorResponsibilitiesGuide = lazy(() => import('@/pages/marketing/guide/ExecutorResponsibilitiesGuide'));
const DeathNotificationServiceGuide = lazy(() => import('@/pages/marketing/guide/DeathNotificationServiceGuide'));
const InstitutionGuide = lazy(() => import('@/pages/marketing/guide/InstitutionGuide'));
const WhatToDoWhenSomeoneDiesGuide = lazy(() => import('@/pages/marketing/guide/WhatToDoWhenSomeoneDiesGuide'));
const HowToApplyForProbateGuide = lazy(() => import('@/pages/marketing/guide/HowToApplyForProbateGuide'));
const CloseBankAccountAfterDeathGuide = lazy(() => import('@/pages/marketing/guide/CloseBankAccountAfterDeathGuide'));
const RegisterADeathGuide = lazy(() => import('@/pages/marketing/guide/RegisterADeathGuide'));
const EstateAdministrationChecklistGuide = lazy(() => import('@/pages/marketing/guide/EstateAdministrationChecklistGuide'));
const BereavementSupportGuide = lazy(() => import('@/pages/marketing/guide/BereavementSupportGuide'));
const LettersOfAdministrationGuide = lazy(() => import('@/pages/marketing/guide/LettersOfAdministrationGuide'));
const InheritanceTaxGuide = lazy(() => import('@/pages/marketing/guide/InheritanceTaxGuide'));
const HowLongDoesProbateTakeGuide = lazy(() => import('@/pages/marketing/guide/HowLongDoesProbateTakeGuide'));
const DigitalEstateGuide = lazy(() => import('@/pages/marketing/guide/DigitalEstateGuide'));
const EstateManagementSoftwareGuide = lazy(() => import('@/pages/marketing/guide/EstateManagementSoftwareGuide'));
const NotifyHMRCAfterDeathGuide = lazy(() => import('@/pages/marketing/guide/NotifyHMRCAfterDeathGuide'));
const CancelDirectDebitsAfterDeathGuide = lazy(() => import('@/pages/marketing/guide/CancelDirectDebitsAfterDeathGuide'));
const WhatIsAnExecutorGuide = lazy(() => import('@/pages/marketing/guide/WhatIsAnExecutorGuide'));
const PensionAfterDeathGuide = lazy(() => import('@/pages/marketing/guide/PensionAfterDeathGuide'));
const GuidesIndexPage = lazy(() => import('@/pages/marketing/GuidesIndexPage'));

// B2B Partner Pages
const B2BRegisterPage = lazy(() => import('@/pages/b2b/B2BRegisterPage'));
const B2BDashboardPage = lazy(() => import('@/pages/b2b/B2BDashboardPage'));
const B2BCasesPage = lazy(() => import('@/pages/b2b/B2BCasesPage'));
const B2BCaseDetailPage = lazy(() => import('@/pages/b2b/B2BCaseDetailPage'));
const B2BReferralsPage = lazy(() => import('@/pages/b2b/B2BReferralsPage'));
const B2BTeamPage = lazy(() => import('@/pages/b2b/B2BTeamPage'));
const B2BSettingsPage = lazy(() => import('@/pages/b2b/B2BSettingsPage'));
const B2BContactSalesPage = lazy(() => import('@/pages/b2b/B2BContactSalesPage'));
const SolicitorDashboardPage = lazy(() => import('@/pages/b2b/SolicitorDashboardPage'));
const B2BAcceptInvitePage = lazy(() => import('@/pages/b2b/B2BAcceptInvitePage'));
const AcceptVaultLinkPage = lazy(() => import('@/pages/b2b/AcceptVaultLinkPage'));
const VaultClientDetailPage = lazy(() => import('@/pages/b2b/VaultClientDetailPage'));
const BankDashboardPage = lazy(() => import('@/pages/b2b/BankDashboardPage'));
const BankNotificationsPage = lazy(() => import('@/pages/b2b/BankNotificationsPage'));
const BankNotificationDetailPage = lazy(() => import('@/pages/b2b/BankNotificationDetailPage'));
const BankSettingsPage = lazy(() => import('@/pages/b2b/BankSettingsPage'));
const BankReportingPage = lazy(() => import('@/pages/b2b/BankReportingPage'));
const ReportingPage = lazy(() => import('@/pages/app/ReportingPage'));
const B2BAccountantCasesPage = lazy(() => import('@/pages/b2b/B2BAccountantCasesPage'));
const B2BAccountantCaseDetailPage = lazy(() => import('@/pages/b2b/B2BAccountantCaseDetailPage'));
const AccountantDashboardPage = lazy(() => import('@/pages/b2b/AccountantDashboardPage'));
const B2BCaseNotificationPage = lazy(() => import('@/pages/b2b/B2BCaseNotificationPage'));
const BillingSetupComplete = lazy(() => import('@/pages/b2b/BillingSetupComplete'));
const ExecutorCaseViewerPage = lazy(() => import('@/pages/b2b/ExecutorCaseViewerPage'));
const AcceptViewerInvitePage = lazy(() => import('@/pages/b2b/AcceptViewerInvitePage'));

// Marketing Pages - Section 19
const ReferredPage = lazy(() => import('@/pages/marketing/ReferredPage'));
const FindPartnerPage = lazy(() => import('@/pages/marketing/FindPartnerPage'));

// Feature Requests & FAQ
const FeatureRequestsPage = lazy(() => import('@/pages/app/FeatureRequestsPage'));
const FAQPage = lazy(() => import('@/pages/app/FAQPage'));

// Public Bank Portal
const BankPortalPage = lazy(() => import('@/pages/BankPortalPage'));
const DemoStartPage = lazy(() => import('@/pages/DemoStartPage'));

// Conditional Dashboard for bank vs non-bank partners
function PartnerDashboardRouter() {
  const { user, loading, organisation } = useAuth();
  
  // Wait for auth loading to complete before deciding which dashboard to show
  if (loading || !user) {
    return <PageLoader />;
  }
  
  // Route each B2B type to their dedicated dashboard based on role
  const role = user?.role;
  
  if (role === 'b2b_bank_partner') return <BankDashboardPage />;
  if (role === 'b2b_accountant') return <AccountantDashboardPage />;
  if (role === 'b2b_solicitor') return <SolicitorDashboardPage />;
  if (role === 'b2b_funeral') return <B2BDashboardPage />; // Funeral homes use referral-focused dashboard
  
  // B2B team members with role='consumer' but has_b2b_membership=true
  // Route them based on their organisation type or default to cases page
  if (user.has_b2b_membership) {
    // Check organisation type if available
    const orgType = organisation?.partner_type;
    if (orgType === 'bank') return <BankDashboardPage />;
    if (orgType === 'accountancy_firm' || orgType === 'accountant') return <AccountantDashboardPage />;
    // Default for team members: show cases page (most common use case)
    return <B2BCasesPage />;
  }
  
  // If user somehow got here without B2B access, redirect to consumer dashboard
  // This should not happen due to ProtectedRoute, but as a safety fallback
  return <Navigate to="/dashboard" replace />;
}

// Conditional Settings for bank vs non-bank partners
function PartnerSettingsRouter() {
  const { user, loading } = useAuth();
  
  // Wait for auth loading to complete
  if (loading || !user) {
    return <PageLoader />;
  }
  
  // Use role to determine settings page - bank partners have b2b_bank_partner role
  const isBank = user?.role === 'b2b_bank_partner';
  
  return isBank ? <BankSettingsPage /> : <B2BSettingsPage />;
}

// B2B roles that can be accessed via org membership - moved to module scope for stable reference
const B2B_ROLES = ['b2b_owner', 'b2b_member', 'b2b_funeral', 'b2b_solicitor', 'b2b_accountant', 'b2b_bank_partner'];

// Admin roles - moved to module scope for stable reference
const ADMIN_ROLES = ['admin', 'super_admin', 'estate_reviewer'];

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading, getActiveRole, getRoleRoute } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    if (!loading && user && allowedRoles) {
      const currentRole = getActiveRole();
      
      // Check if this is a B2B route and user has B2B membership
      const isB2BRoute = allowedRoles.some(role => B2B_ROLES.includes(role));
      const hasB2BMembership = user.has_b2b_membership === true;
      
      // Allow access if: role matches OR (B2B route AND has B2B membership)
      const hasAccess = allowedRoles.includes(currentRole) || (isB2BRoute && hasB2BMembership);
      
      if (!hasAccess) {
        setAccessDenied(true);
        const timer = setTimeout(() => {
          // If user has B2B membership, route to partners dashboard
          if (hasB2BMembership) {
            navigate('/partners/dashboard', { replace: true });
          } else {
            navigate(getRoleRoute(currentRole), { replace: true });
          }
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [loading, user, allowedRoles, getActiveRole, getRoleRoute, navigate, location]);

  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/auth/login" replace />;
  
  if (accessDenied) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--safekept-app-bg)]">
        <div className="text-center">
          <p className="text-lg font-semibold text-slate-700 dark:text-slate-300">You do not have access to this section</p>
          <p className="text-sm text-slate-500 mt-2">Redirecting to your dashboard...</p>
        </div>
      </div>
    );
  }
  
  return children;
}

// AdminRoute - explicit guard for admin-only routes
function AdminRoute({ children }) {
  const { user, loading, getActiveRole, getRoleRoute } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!loading && user) {
      const currentRole = getActiveRole();
      if (!ADMIN_ROLES.includes(currentRole)) {
        // Immediately redirect non-admin users
        navigate(getRoleRoute(currentRole), { replace: true });
      }
    }
  }, [loading, user, getActiveRole, getRoleRoute, navigate]);
  
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/auth/login" replace />;
  
  const currentRole = getActiveRole();
  if (!ADMIN_ROLES.includes(currentRole)) {
    return null; // Prevent any flash of admin content
  }
  
  return children;
}

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--safekept-app-bg)]">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-3 border-teal-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-slate-500 font-medium" style={{ fontFamily: 'var(--font-body)' }}>Loading SafeKept...</p>
      </div>
    </div>
  );
}

// PendingTrialGate - catches any user who has pending_plan but slipped to dashboard
function PendingTrialGate({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // Executors bypass this gate entirely - they have paid £149
  if (!loading && user && user.account_type === 'executor') {
    return children;
  }
  
  // Check if user should be redirected to trial activation
  if (!loading && user) {
    const pendingPlan = localStorage.getItem('pending_plan');
    const isOnActivateTrial = location.pathname === '/app/activate-trial';
    const isFreeUser = !user.subscription_tier || user.subscription_tier === 'free' || user.subscription_tier === 'starter';
    const notInTrial = !user.is_in_trial;
    
    if (pendingPlan && (pendingPlan === 'guardian' || pendingPlan === 'estate_pro') && 
        isFreeUser && notInTrial && !isOnActivateTrial) {
      return <Navigate to={`/app/activate-trial?plan=${pendingPlan}`} replace />;
    }
  }
  
  return children;
}

// SECTION 9: ExecutorPaymentGate - forces executor users to pay before accessing app
function ExecutorPaymentGate({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) return <PageLoader />;
  
  // Demo users bypass payment gate entirely
  if (user?.is_demo) return children;
  
  // Only apply to executor account types
  if (!user || user.account_type !== 'executor') {
    return children;
  }
  
  // Allow access to the payment gate itself and extension page
  const allowedPaths = ['/app/activate-estate', '/app/executor-extend'];
  if (allowedPaths.some(p => location.pathname.startsWith(p))) {
    return children;
  }
  
  // Check if executor has valid, non-expired access
  const hasValidAccess = user.executor_access_expires_at && 
    new Date(user.executor_access_expires_at) > new Date() &&
    !user.executor_refunded;
  
  if (!hasValidAccess) {
    return <Navigate to="/app/activate-estate" replace />;
  }
  
  return children;
}

function AppRoutes() {
  const { user, loading, getActiveRole, getRoleRoute, showTimeoutWarning, refreshSession, logout } = useAuth();
  const { show402Modal } = usePlanLimit();
  
  // Register global 402 handler
  useEffect(() => {
    setGlobal402Handler(show402Modal);
    return () => setGlobal402Handler(null);
  }, [show402Modal]);

  if (loading) return <PageLoader />;

  const currentRole = user ? getActiveRole() : null;
  const defaultDashboard = currentRole ? getRoleRoute(currentRole) : '/dashboard';

  return (
    <Suspense fallback={<PageLoader />}>
      <OfflineBanner />
      <SessionTimeoutWarning
        isOpen={!!showTimeoutWarning}
        minutesRemaining={2}
        onStayLoggedIn={refreshSession}
        onLogOut={logout}
      />
      <Routes>
        {/* Marketing */}
        <Route path="/" element={<HomePage />} />
        <Route path="/death-notification" element={<DeathNotificationPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/research" element={<ResearchPage />} />
        <Route path="/blog" element={<BlogIndexPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />

        {/* SEO Guide Pages */}
        <Route path="/guides" element={<GuidesIndexPage />} />
        <Route path="/guide/how-to-notify-banks-after-death" element={<HowToNotifyBanksGuide />} />
        <Route path="/guide/what-is-tell-us-once" element={<TellUsOnceGuide />} />
        <Route path="/guide/what-is-grant-of-probate" element={<GrantOfProbateGuide />} />
        <Route path="/guide/executor-responsibilities-uk" element={<ExecutorResponsibilitiesGuide />} />
        <Route path="/guide/what-is-the-death-notification-service" element={<DeathNotificationServiceGuide />} />
        <Route path="/guide/what-to-do-when-someone-dies-uk" element={<WhatToDoWhenSomeoneDiesGuide />} />
        <Route path="/guide/how-to-apply-for-probate-uk" element={<HowToApplyForProbateGuide />} />
        <Route path="/guide/close-bank-account-after-death-uk" element={<CloseBankAccountAfterDeathGuide />} />
        <Route path="/guide/register-a-death-uk" element={<RegisterADeathGuide />} />
        <Route path="/guide/estate-administration-checklist-uk" element={<EstateAdministrationChecklistGuide />} />
        <Route path="/guide/bereavement-support-uk" element={<BereavementSupportGuide />} />
        <Route path="/guide/letters-of-administration-uk" element={<LettersOfAdministrationGuide />} />
        <Route path="/guide/inheritance-tax-uk" element={<InheritanceTaxGuide />} />
        <Route path="/guide/how-long-does-probate-take-uk" element={<HowLongDoesProbateTakeGuide />} />
        <Route path="/guide/digital-estate-uk" element={<DigitalEstateGuide />} />
        <Route path="/guide/estate-management-software-uk" element={<EstateManagementSoftwareGuide />} />
        <Route path="/guide/notify-hmrc-after-death-uk" element={<NotifyHMRCAfterDeathGuide />} />
        <Route path="/guide/cancel-direct-debits-after-death-uk" element={<CancelDirectDebitsAfterDeathGuide />} />
        <Route path="/guide/what-is-an-executor-uk" element={<WhatIsAnExecutorGuide />} />
        <Route path="/guide/pension-after-death-uk" element={<PensionAfterDeathGuide />} />
        <Route path="/guide/notifying-:slug-after-death" element={<InstitutionGuide />} />

        {/* Auth */}
        <Route path="/auth/login" element={user ? <Navigate to={defaultDashboard} replace /> : <LoginPage />} />
        <Route path="/auth/signup" element={user ? <Navigate to={defaultDashboard} replace /> : <SignupPage />} />
        <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/auth/mfa-challenge" element={<MFAChallengePage />} />
        <Route path="/app/mfa-setup" element={<ProtectedRoute><MFASetupPage /></ProtectedRoute>} />

        {/* Executor invitation accept */}
        <Route path="/executors/accept/:token" element={<AcceptInvitation />} />
        
        {/* Case link executor invitation landing page */}
        <Route path="/invite/executor/:token" element={<ExecutorInvitePage />} />

        {/* Onboarding */}
        <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />

        {/* Trial Activation - standalone page with no navigation */}
        <Route path="/app/activate-trial" element={<ProtectedRoute><ActivateTrialPage /></ProtectedRoute>} />
        
        {/* SECTION 8 & 9: Executor Payment Gate */}
        <Route path="/app/activate-estate" element={<ProtectedRoute><ActivateEstatePage /></ProtectedRoute>} />
        <Route path="/app/executor-extend" element={<ProtectedRoute><ExecutorPaymentGate><ExecutorExtendPage /></ExecutorPaymentGate></ProtectedRoute>} />

        {/* Consumer routes - wrapped with PendingTrialGate to catch users who slipped through */}
        <Route path="/dashboard" element={<ProtectedRoute allowedRoles={['consumer']}><PendingTrialGate><DashboardPage /></PendingTrialGate></ProtectedRoute>} />
        <Route path="/vault" element={<ProtectedRoute allowedRoles={['consumer']}><PendingTrialGate><VaultPage /></PendingTrialGate></ProtectedRoute>} />
        <Route path="/crypto" element={<ProtectedRoute allowedRoles={['consumer', 'executor']}><PendingTrialGate><CryptoPage /></PendingTrialGate></ProtectedRoute>} />
        <Route path="/documents" element={<ProtectedRoute allowedRoles={['consumer', 'executor']}><PendingTrialGate><DocumentsPage /></PendingTrialGate></ProtectedRoute>} />
        <Route path="/subscriptions" element={<ProtectedRoute allowedRoles={['consumer']}><PendingTrialGate><SubscriptionsPage /></PendingTrialGate></ProtectedRoute>} />
        <Route path="/executors" element={<ProtectedRoute allowedRoles={['consumer']}><PendingTrialGate><ExecutorsPage /></PendingTrialGate></ProtectedRoute>} />
        <Route path="/integrations" element={<ProtectedRoute allowedRoles={['consumer']}><PendingTrialGate><IntegrationsPage /></PendingTrialGate></ProtectedRoute>} />

        {/* Executor routes - SECTION 9: wrapped with ExecutorPaymentGate */}
        <Route path="/dashboard/executor" element={<ProtectedRoute allowedRoles={['executor']}><ExecutorPaymentGate><ExecutorDashboardPage /></ExecutorPaymentGate></ProtectedRoute>} />
        <Route path="/estate/*" element={<ProtectedRoute allowedRoles={['executor', 'super_admin', 'estate_reviewer']}><ExecutorPaymentGate><EstatePage /></ExecutorPaymentGate></ProtectedRoute>} />

        {/* B2B Partner routes - redirect old paths to correct /partners/dashboard */}
        <Route path="/dashboard/partner/funeral" element={<Navigate to="/partners/dashboard" replace />} />
        <Route path="/dashboard/partner/solicitor" element={<Navigate to="/partners/dashboard" replace />} />
        <Route path="/dashboard/partner/accountant" element={<Navigate to="/partners/dashboard" replace />} />
        <Route path="/dashboard/partner/bank" element={<Navigate to="/partners/dashboard" replace />} />

        {/* Partner Platform Pages - Public */}
        <Route path="/partners/register" element={<B2BRegisterPage />} />
        <Route path="/partners/contact-sales" element={<B2BContactSalesPage />} />
        <Route path="/partners/accept-invite" element={<B2BAcceptInvitePage />} />
        <Route path="/partners/accept-vault-link" element={<AcceptVaultLinkPage />} />
        <Route path="/partners/vault-clients/:link_id" element={<ProtectedRoute><VaultClientDetailPage /></ProtectedRoute>} />
        <Route path="/invite/accept" element={<B2BAcceptInvitePage />} />
        <Route path="/link/accept" element={<ProtectedRoute><B2BAcceptInvitePage /></ProtectedRoute>} />
        
        {/* Partner Platform Pages - Protected */}
        <Route path="/partners/dashboard" element={<ProtectedRoute allowedRoles={['b2b_owner', 'b2b_member', 'b2b_funeral', 'b2b_solicitor', 'b2b_accountant', 'b2b_bank_partner']}><PartnerDashboardRouter /></ProtectedRoute>} />
        <Route path="/partners/cases" element={<ProtectedRoute allowedRoles={['b2b_owner', 'b2b_member', 'b2b_funeral', 'b2b_solicitor', 'b2b_accountant']}><B2BCasesPage /></ProtectedRoute>} />
        <Route path="/partners/cases/:case_id" element={<ProtectedRoute allowedRoles={['b2b_owner', 'b2b_member', 'b2b_funeral', 'b2b_solicitor', 'b2b_accountant']}><B2BCaseDetailPage /></ProtectedRoute>} />
        <Route path="/partners/referrals" element={<ProtectedRoute allowedRoles={['b2b_owner', 'b2b_member', 'b2b_funeral', 'b2b_solicitor', 'b2b_accountant']}><B2BReferralsPage /></ProtectedRoute>} />
        <Route path="/partners/team" element={<ProtectedRoute allowedRoles={['b2b_owner', 'b2b_member', 'b2b_funeral', 'b2b_solicitor', 'b2b_accountant', 'b2b_bank_partner']}><B2BTeamPage /></ProtectedRoute>} />
        <Route path="/partners/settings" element={<ProtectedRoute allowedRoles={['b2b_owner', 'b2b_member', 'b2b_funeral', 'b2b_solicitor', 'b2b_accountant', 'b2b_bank_partner']}><PartnerSettingsRouter /></ProtectedRoute>} />
        <Route path="/partners/feature-requests" element={<ProtectedRoute><FeatureRequestsPage /></ProtectedRoute>} />
        
        {/* Bank Partner Routes */}
        <Route path="/partners/notifications" element={<ProtectedRoute allowedRoles={['b2b_bank_partner']}><BankNotificationsPage /></ProtectedRoute>} />
        <Route path="/partners/notifications/:caseId" element={<ProtectedRoute allowedRoles={['b2b_bank_partner']}><BankNotificationDetailPage /></ProtectedRoute>} />
        <Route path="/partners/reporting" element={<ProtectedRoute allowedRoles={['b2b_bank_partner']}><BankReportingPage /></ProtectedRoute>} />
        <Route path="/partners/insights" element={<ProtectedRoute allowedRoles={['b2b_solicitor', 'b2b_accountant', 'b2b_owner', 'b2b_member']}><ReportingPage /></ProtectedRoute>} />
        <Route path="/partners/bank/dashboard" element={<ProtectedRoute allowedRoles={['b2b_bank_partner']}><BankDashboardPage /></ProtectedRoute>} />
        <Route path="/partners/bank/settings" element={<ProtectedRoute allowedRoles={['b2b_bank_partner']}><BankSettingsPage /></ProtectedRoute>} />
        
        <Route path="/partners/accountant/dashboard" element={<ProtectedRoute allowedRoles={['b2b_accountant', 'b2b_owner', 'b2b_member']}><AccountantDashboardPage /></ProtectedRoute>} />
        <Route path="/partners/accountant/cases" element={<ProtectedRoute allowedRoles={['b2b_accountant', 'b2b_owner', 'b2b_member']}><B2BAccountantCasesPage /></ProtectedRoute>} />
        <Route path="/partners/accountant/cases/:case_id" element={<ProtectedRoute allowedRoles={['b2b_accountant', 'b2b_owner', 'b2b_member']}><B2BAccountantCaseDetailPage /></ProtectedRoute>} />
        <Route path="/partners/cases/:case_id/notifications" element={<ProtectedRoute allowedRoles={['b2b_owner', 'b2b_member', 'b2b_solicitor', 'b2b_accountant']}><B2BCaseNotificationPage /></ProtectedRoute>} />
        <Route path="/partners/viewer/:case_id" element={<ProtectedRoute><ExecutorCaseViewerPage /></ProtectedRoute>} />
        <Route path="/partners/accept-viewer-invite" element={<AcceptViewerInvitePage />} />
        
        {/* Billing Setup Complete - SCA/3DS redirect handler */}
        <Route path="/b2b/billing/setup-complete" element={<ProtectedRoute allowedRoles={['b2b_owner', 'b2b_member', 'b2b_funeral', 'b2b_solicitor', 'b2b_accountant']}><BillingSetupComplete /></ProtectedRoute>} />

        {/* Public Marketing Pages */}
        <Route path="/referred" element={<ReferredPage />} />
        <Route path="/ref/:code" element={<ReferralRedirect />} />
        <Route path="/find-a-partner" element={<FindPartnerPage />} />

        {/* Public Bank Portal - NO AUTH REQUIRED */}
        <Route path="/bank-portal/:token" element={<BankPortalPage />} />

        {/* Public Demo Start Page - NO AUTH REQUIRED */}
        <Route path="/demo/start" element={<DemoStartPage />} />

        {/* Admin routes - protected by AdminRoute guard */}
        <Route path="/dashboard/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
        <Route path="/admin/*" element={<AdminRoute><AdminPage /></AdminRoute>} />

        {/* Settings - all authenticated users */}
        <Route path="/settings" element={<ProtectedRoute><PendingTrialGate><SettingsPage /></PendingTrialGate></ProtectedRoute>} />

        {/* Help Section - all authenticated users */}
        <Route path="/app/faq" element={<ProtectedRoute><FAQPage /></ProtectedRoute>} />
        <Route path="/app/feature-requests" element={<ProtectedRoute><FeatureRequestsPage /></ProtectedRoute>} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  // Consent-gated PostHog initialisation. PostHog is opted out by default and
  // only starts capturing once the user accepts analytics in the cookie banner.
  // We listen for both the existing 'safekept_consent_granted' event (dispatched
  // by CookieBanner.js) and a forward-compatible 'posthog:consent' alias.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    initPostHog();

    // Apply previously-stored consent (returning visitors)
    try {
      const raw = localStorage.getItem('safekept_cookie_consent');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.analytics === true) posthogOptIn();
        else if (parsed?.analytics === false) posthogOptOut();
      }
    } catch (_) {
      /* ignore corrupt consent record */
    }

    const handleConsent = (event) => {
      const accepted = event?.detail?.analytics ?? event?.detail?.accepted;
      initPostHog();
      if (accepted) posthogOptIn();
      else posthogOptOut();
    };

    window.addEventListener('safekept_consent_granted', handleConsent);
    window.addEventListener('posthog:consent', handleConsent);
    return () => {
      window.removeEventListener('safekept_consent_granted', handleConsent);
      window.removeEventListener('posthog:consent', handleConsent);
    };
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <ThemeProvider>
              <SecurityProvider>
                <BillingProvider>
                  <PlanLimitProvider>
                    <AppRoutes />
                    <SecurityModals />
                    <Toaster position="top-right" richColors />
                    <CookieBanner />
                  </PlanLimitProvider>
                </BillingProvider>
              </SecurityProvider>
            </ThemeProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
